<script>
  import { onMount } from "svelte";
  import * as d3 from "d3";
  import { selectedStation, ozoneValues } from ".././store"; // Import the store

  export let landkreiseData; // GeoJSON for landkreise
  export let bundeslaenderData; // GeoJSON for bundeslaender
  export let stationsData; // GeoJSON for bundeslaender

  $: $selectedStation;
  $: $ozoneValues;

  let svg, zoom, colorScale, g;

  // Variable zum Speichern der Tooltip-Information
  let pendingTooltip = null;

  function findValidValue(stationId) {
    //console.log(`Suche gültigen Wert für Station: ${stationId}`);
    for (let i = 73; i >= 0; i--) {
      // Starte bei Index 73 und gehe rückwärts
      const value = $ozoneValues[i]?.[stationId];
      //console.log(`Index: ${i}, Wert: ${value}`);
      if (value !== undefined && value !== null && value !== "" && !isNaN(+value) && +value !== 0) {
        //console.log(`Gültiger Wert gefunden: ${value} bei Index ${i}`);
        return +value; // Gültigen Wert gefunden
      }
    }
    //console.log(`Kein gültiger Wert gefunden für Station: ${stationId}`);
    return null; // Kein gültiger Wert gefunden
  }

  // Ozonwerte den Stationen zuordnen und Stationen ohne Werte herausfiltern
  $: validStations = stationsData
    .map((station) => {
      const stationId = station.Station;
      const value = findValidValue(stationId); // Suche den gültigen Wert ab Index 73 rückwärts
      return { ...station, value: value !== null ? +value : null };
    })
    .filter((station) => station.value !== null); // Stationen ohne gültigen Wert herausfiltern

  // Feste Farbskala für Ozonwerte (0-240 µg/m³)
  const fixedColorScale = d3
    .scaleLinear()
    .domain([0, 40, 80, 120, 160, 200, 240])
    .range(["#2A5589", "#83A8D3", "#95B5DA", "#DE9469", "#B56332", "#913701", "#702E1F"])
    .interpolate(d3.interpolateRgb);

  // Farbfunktion die Werte über 240 behandelt
  function getOzoneColor(value) {
    if (value === null || value === undefined || isNaN(value)) {
      return "rgba(200, 200, 200, 0.7)"; // Grau für fehlende Werte
    }
    if (value > 240) {
      return "#5C1F24"; // Spezielle Farbe für Werte über 240
    }
    return fixedColorScale(value);
  }

  // Feste Legendenwerte
  let legendMin = 0;
  let legendMax = 240;

  $: if (fixedColorScale && $ozoneValues.length > 0) {
    //console.log("Hexagon-Farben werden aktualisiert...");
    d3.selectAll(".station-hexagon").style("fill", (d) => {
      const value = d.value;
      const color = getOzoneColor(value);
      const d3Color = d3.color(color);
      if (d3Color) {
        d3Color.opacity = 0.7;
        return d3Color.toString();
      }
      return "rgba(200, 200, 200, 0.5)";
    });
  }

  // Function to handle clicks on points
  function handlePointClick(station, event, showTooltip = true) {
    //console.log("Station clicked:", station, "Value:", station.value);
    selectedStation.set(station.Station); // Update the store with the clicked station
    closeTooltip(); // Schließe den Tooltip

    // NEU: Prüfe, ob es ein Mobile-Gerät ist
    const isMobile = window.matchMedia("(max-width: 768px)").matches;

    // Speichere Event und Station für späteren Tooltip nur auf Mobile
    if (showTooltip && event && isMobile) {
      pendingTooltip = { event, station };
    } else {
      pendingTooltip = null;
    }
  }

  // Funktion zum Schließen des Tooltips
  function closeTooltip() {
    d3.select(".hex-tooltip").style("display", "none");
  }

  // Funktion zum Positionieren des Tooltips
  function positionTooltip(tooltip, event) {
    const tooltipNode = tooltip.node();
    const [mouseX, mouseY] = d3.pointer(event, document.querySelector(".map-wrapper"));
    const wrapper = document.querySelector(".map-wrapper");
    const wrapperRect = wrapper.getBoundingClientRect();

    const tooltipWidth = tooltipNode.offsetWidth;
    const tooltipHeight = tooltipNode.offsetHeight;
    const tooltipMargin = 15;

    let left = mouseX + 20;
    let top = mouseY - 10;

    if (mouseX + tooltipWidth + tooltipMargin > wrapperRect.width) {
      left = mouseX - tooltipWidth - 20;
    }

    if (mouseY + tooltipHeight + tooltipMargin > wrapperRect.height) {
      top = mouseY - tooltipHeight - 20;
    }

    if (top < tooltipMargin) top = tooltipMargin;
    if (left < tooltipMargin) left = tooltipMargin;

    tooltip.style("left", `${left}px`).style("top", `${top}px`);
  }

  // Funktion zum Anzeigen des Tooltips für eine Station
  function showTooltipForStation(station, event) {
    const parseDate = d3.timeParse("%d.%m.%y %H:%M");
    const stationId = station.Station;
    const tooltip = d3.select(".hex-tooltip");

    // Extrahiere Prognosedaten (Index > 72)
    const timeseries = $ozoneValues
      .slice(72) // Nur Prognosedaten
      .map((row) => ({
        value: row[stationId] === "" || +row[stationId] === 0 || isNaN(+row[stationId]) ? null : +row[stationId],
        date: parseDate(row.Datum),
      }))
      .filter((d) => d.value !== null && !isNaN(d.value) && d.date);

    tooltip.style("display", "block");

    if (timeseries.length === 0) {
      // Keine Prognosedaten verfügbar
      tooltip.html(`
        <div style="display:flex; justify-content:space-between; align-items:center;">
          <div class="tooltip-title">${station.Stadt || "Station"}</div>
          <button class="tooltip-close" id="tooltip-close-btn" aria-label="Tooltip schließen">&times;</button>
        </div>
        <div class="tooltip-text">
          Keine Prognosedaten verfügbar.<br>
          Aktueller Ozonwert: <strong>${station.value} µg/m³</strong>
        </div>
      `);
    } else {
      // Prognosedaten vorhanden -> Liniendiagramm anzeigen
      tooltip.html(`
        <div style="display:flex; justify-content:space-between; align-items:center;">
          <div class="tooltip-title">${station.Stadt || "Station"}</div>
          <button class="tooltip-close" id="tooltip-close-btn" aria-label="Tooltip schließen">&times;</button>
        </div>
        <div class="tooltip-text">
          Aktueller Ozonwert mit Prognose in µg/m³
        </div>
        <div id="tooltip-chart" style="margin-top:8px;"></div>
        
      `);

      // Zeichne das Liniendiagramm
      drawTooltipChart(timeseries);
    }

    // Tooltip schließen-Button
    setTimeout(() => {
      const closeBtn = document.getElementById("tooltip-close-btn");
      if (closeBtn) {
        closeBtn.onclick = closeTooltip;
      }
    }, 0);

    // Mobile "Mehr Werte anzeigen" Link
    if (window.matchMedia("(max-width: 600px)").matches) {
      const showMoreDiv = document.getElementById("show-more-mobile");
      if (showMoreDiv) {
        showMoreDiv.style.display = "block";
        const showMoreLink = showMoreDiv.querySelector("a");
        if (showMoreLink) {
          showMoreLink.onclick = (e) => {
            e.preventDefault();

            const target = document.getElementById("ozone-wrapper");
            console.log("tooltip-link-target", target, document.documentElement.style);
            if (target) {
              if ("scrollTo" in window && typeof window.scrollTo === "function") {
                console.log("scroll 1");
                window.scrollTo({ top: target.offsetTop, behavior: "smooth" });
              } else {
                console.log("scroll 2");
                window.scrollTo(0, target.offsetTop);
              }
            }
            closeTooltip();
          };
        }
      }
    }

    // Tooltip positionieren
    positionTooltipAtStation(tooltip, station);
  }

  // Neue Funktion zum Positionieren des Tooltips basierend auf der Station-Position
  function positionTooltipAtStation(tooltip, station) {
    const tooltipNode = tooltip.node();
    const wrapper = document.querySelector(".map-wrapper");
    const wrapperRect = wrapper.getBoundingClientRect();

    // Hole die aktuelle transformierte Position der Station
    const currentTransform = d3.zoomTransform(svg);
    const [baseX, baseY] = projection([station.Long, station.Lat]);

    // Wende die aktuelle Zoom-Transformation an
    const transformedX = currentTransform.applyX(baseX);
    const transformedY = currentTransform.applyY(baseY);

    const tooltipWidth = tooltipNode.offsetWidth;
    const tooltipHeight = tooltipNode.offsetHeight;
    const tooltipMargin = 15;

    // Standardposition: rechts neben der Station
    let left = transformedX + 20;
    let top = transformedY - 10;

    // Prüfe, ob der Tooltip rechts über den Rand hinausgeht
    if (transformedX + tooltipWidth + tooltipMargin > wrapperRect.width) {
      left = transformedX - tooltipWidth - 20; // Links von der Station
    }

    // Prüfe, ob der Tooltip unten über den Rand hinausgeht
    if (transformedY + tooltipHeight + tooltipMargin > wrapperRect.height) {
      top = transformedY - tooltipHeight - 20; // Oberhalb der Station
    }

    // Verhindere, dass der Tooltip oben oder links am Rand klebt
    if (top < tooltipMargin) top = tooltipMargin;
    if (left < tooltipMargin) left = tooltipMargin;

    // Verhindere, dass der Tooltip rechts oder unten über den Rand hinausgeht
    if (left + tooltipWidth > wrapperRect.width - tooltipMargin) {
      left = wrapperRect.width - tooltipWidth - tooltipMargin;
    }
    if (top + tooltipHeight > wrapperRect.height - tooltipMargin) {
      top = wrapperRect.height - tooltipHeight - tooltipMargin;
    }

    tooltip.style("left", `${left}px`).style("top", `${top}px`);
  }

  let zoomIn = () => {};
  let zoomOut = () => {};
  let resetZoom = () => {};
  let projection;

  onMount(() => {
    // Set up dimensions and projection
    const width = svg.clientWidth;
    const height = svg.clientHeight;

    projection = d3.geoMercator().fitSize([width, height], bundeslaenderData);

    const path = d3.geoPath().projection(projection);

    const svgElement = d3.select(svg);
    g = svgElement.append("g");

    // Draw Landkreise (foreground layer)
    g.selectAll(".landkreise")
      .data(landkreiseData.features)
      .enter()
      .append("path")
      .attr("class", "landkreise")
      .attr("d", path);

    g.selectAll(".bundeslaender")
      .data(bundeslaenderData.features)
      .enter()
      .append("path")
      .attr("class", "bundeslaender")
      .attr("d", path);

    // Funktion, um den Pfad für ein Hexagon zu generieren
    function createHexagonPath(x, y, size) {
      const angle = Math.PI / 3; // 60 Grad
      const points = Array.from({ length: 6 }, (_, i) => [
        x + size * Math.cos(i * angle),
        y + size * Math.sin(i * angle),
      ]);
      return d3.line()(points.concat([points[0]])); // Schließt das Hexagon
    }

    // Hexagone hinzufügen
    g.selectAll(".station-hexagon")
      .data(validStations)
      .enter()
      .append("path")
      .attr("class", "station-hexagon")
      .attr("d", (d) =>
        createHexagonPath(
          projection([d.Long, d.Lat])[0], // x-Koordinate
          projection([d.Long, d.Lat])[1], // y-Koordinate
          7, // Größe des Hexagons
        ),
      )
      .style("fill", (d) => {
        const value = d.value;
        const color = value !== null && value !== undefined ? getOzoneColor(value) : "rgba(200, 200, 200, 0.7)"; // Standardfarbe für fehlende Werte
        //console.log("Station:", d.Station, "Wert:", value, "Farbe:", color);
        return d3.color(color).copy({ opacity: 0.7 }); // Transparenz auf 50 % setzen
      })
      .style("stroke", "grey") // Randfarbe
      .style("stroke-width", 1)
      .on("click", (event, d) => {
        const isMobile = window.matchMedia("(max-width: 768px)").matches;
        handlePointClick(d, event, isMobile); // Tooltip nur auf Mobile
      });

    selectedStation.subscribe((stationId) => {
      g.selectAll(".station-point").style("fill", (d) => (d.Station === stationId ? "lime" : "red"));
    });

    // Tooltip schließen, wenn selectedStation sich ändert
    selectedStation.subscribe(() => {
      closeTooltip();
    });

    // Set up zoom behavior
    zoom = d3
      .zoom()
      .scaleExtent([1, 5])
      .filter((event) => {
        // Desktop: Alle Events erlauben
        if (event.type === "wheel" || event.type === "mousedown") return true;

        // Mobile: Nur Multi-Touch für Zoom/Pan
        if (event.type === "touchstart") {
          return event.touches && event.touches.length > 1;
        }

        // Touch-Move: Nur bei Multi-Touch
        if (event.type === "touchmove") {
          return event.touches && event.touches.length > 1;
        }

        return false;
      })
      .on("zoom", (event) => {
        g.attr("transform", event.transform); // Apply zoom transformation
        // Hexagone gegenskalieren
        g.selectAll(".station-hexagon").attr("transform", function (d) {
          // Hole die Position des Hexagons
          const [x, y] = projection([d.Long, d.Lat]);
          const k = event.transform.k;
          // Skalierung um den Mittelpunkt des Hexagons
          return `translate(${x},${y}) scale(${1 / k}) translate(${-x},${-y})`;
        });
        // Landkreise-Linienstärke anpassen
        g.selectAll(".landkreise").style("stroke-width", 0.6 / event.transform.k);

        // Bundeslandlinien-Linienstärke anpassen (mit Mindestwert)
        g.selectAll(".bundeslaender").style("stroke-width", 1.5 / event.transform.k);
      });

    const isTouch = window.matchMedia("(pointer: coarse)").matches;

    svgElement.call(zoom); // Bind zoom behavior to the SVG

    // Touch-Event-Handler für bessere Mobile-Kontrolle
    if (window.matchMedia("(max-width: 768px)").matches) {
      svgElement.node().addEventListener(
        "touchstart",
        (event) => {
          if (event.touches.length === 1) {
            // Single-Touch: Stoppe Propagation, damit Seiten-Scroll funktioniert
            event.stopPropagation();
          }
        },
        { passive: true },
      );

      svgElement.node().addEventListener(
        "touchmove",
        (event) => {
          if (event.touches.length === 1) {
            // Single-Touch: Erlaube Seiten-Scroll
            event.stopPropagation();
          }
        },
        { passive: true },
      );
    }

    // Zoom in and out functions
    zoomIn = () => {
      svgElement.transition().call(zoom.scaleBy, 1.5); // Zoom in by 1.5x
    };

    zoomOut = () => {
      svgElement.transition().call(zoom.scaleBy, 0.75); // Zoom out by 0.75x
    };

    // Reset zoom function
    resetZoom = () => {
      // Setze die Transformation des Zoom-Verhaltens zurück
      svgElement.transition().duration(500).call(zoom.transform, d3.zoomIdentity);
    };
  });

  function zoomToStation(station) {
    if (!station || !projection || !zoom || !svg) return Promise.resolve();

    const [x, y] = projection([station.Long, station.Lat]);
    const zoomLevel = 2.5;
    const width = svg.clientWidth;
    const height = svg.clientHeight;
    const translateX = width / 2 - x * zoomLevel;
    const translateY = height / 2 - y * zoomLevel;

    return new Promise((resolve) => {
      d3.select(svg)
        .transition()
        .duration(750)
        .call(zoom.transform, d3.zoomIdentity.translate(translateX, translateY).scale(zoomLevel))
        .on("end", resolve); // Promise wird aufgelöst, wenn Transition beendet ist
    });
  }

  import { tick } from "svelte";

  $: if (g && projection && zoom && svg && $selectedStation) {
    const station = validStations.find((s) => s.Station === $selectedStation);
    if (station) {
      tick().then(async () => {
        await zoomToStation(station);

        // Prüfe, ob ein Tooltip nach dem Zoom gezeigt werden soll
        if (pendingTooltip && pendingTooltip.station.Station === station.Station) {
          const { event } = pendingTooltip;
          showTooltipForStation(station, event);
          pendingTooltip = null; // Zurücksetzen
        }
      });
    }
  }

  $: if (g && stationsData) {
    g.selectAll(".station-hexagon")
      .style("stroke-width", (d) => (d.Station === $selectedStation ? 2 : 1)) // Dickeren Rand für die ausgewählte Station
      .style("stroke", (d) =>
        d.Station === $selectedStation
          ? window.matchMedia("(prefers-color-scheme: dark)").matches
            ? "white"
            : "black"
          : "grey",
      )
      .style("fill-opacity", (d) => (d.Station === $selectedStation ? 1 : 0.7)) // Volle Füllung für die ausgewählte Station
      .style("fill", (d) => {
        if (d.Station === $selectedStation) {
          return d3.color(getOzoneColor(d.value)).copy({ opacity: 1 });
        }
        return d3.color(getOzoneColor(d.value)).copy({ opacity: 0.8 });
      });

    // Das ausgewählte Hexagon nach vorne holen
    const selected = g.selectAll(".station-hexagon").filter((d) => d.Station === $selectedStation);
    if (!selected.empty()) {
      selected.raise(); // D3-Funktion: Element nach vorne holen
    }
  }

  function drawTooltipChart(timeseries) {
    const chartContainer = d3.select("#tooltip-chart");
    chartContainer.selectAll("*").remove();

    //margin right darf nicht geringer sein, sonst wird x-Achsen-Beschriftung abgeschnitten
    const width = 220,
      height = 100;
    const margin = { top: 10, right: 42, bottom: 28, left: 28 };

    const svg = chartContainer.append("svg").attr("width", width).attr("height", height);

    const defs = svg.append("defs");
    let gIdx = 0; // eindeutige IDs

    function drawGradientSegment(p1, p2, dashed = false) {
      const id = `t-grad-${gIdx++}`;

      defs
        .append("linearGradient")
        .attr("id", id)
        .attr("gradientUnits", "userSpaceOnUse")
        .attr("x1", x(p1.date))
        .attr("y1", y(p1.value))
        .attr("x2", x(p2.date))
        .attr("y2", y(p2.value))
        .call((g) => {
          g.append("stop").attr("offset", "0%").attr("stop-color", getOzoneColor(p1.value));
          g.append("stop").attr("offset", "100%").attr("stop-color", getOzoneColor(p2.value));
        });

      svg
        .append("path")
        .datum([p1, p2])
        .attr("fill", "none")
        .attr("stroke", `url(#${id})`)
        .attr("stroke-width", 2)
        .attr("stroke-dasharray", dashed ? "6,4" : null)
        .attr(
          "d",
          d3
            .line()
            .x((d) => x(d.date))
            .y((d) => y(d.value)),
        );
    }

    const x = d3
      .scaleTime()
      .domain(d3.extent(timeseries, (d) => d.date))
      .range([margin.left, width - margin.right]);

    const y = d3
      .scaleLinear()
      .domain([0, d3.max(timeseries, (d) => +d.value) * 1.1])
      .range([height - margin.bottom, margin.top]);

    // --- Gitterlinien (horizontal für Y-Achse) ---
    //svg
    //  .append("g")
    //  .attr("class", "grid")
    //  .attr("transform", `translate(${margin.left},0)`)
    //  .call(
    //    d3
    //      .axisLeft(y)
    //      .ticks(3)
    //      .tickSize(-(width - margin.left - margin.right)) // Gitterlinien über gesamte Breite
    //      .tickFormat(""), // Keine Labels für die Gitterlinien
    //  )
    //  .call((g) => g.select(".domain").remove()) // Entfernt die schwarze Achsenlinie
    //  .call((g) => g.selectAll("line"));

    // --- Achsenlinien ---
    // X-Achse
    svg
      .append("line")
      .attr("class", "axis-line x-axis") // Klasse für die X-Achse
      .attr("x1", margin.left)
      .attr("x2", width - margin.right)
      .attr("y1", height - margin.bottom)
      .attr("y2", height - margin.bottom);

    // Y-Achse
    svg
      .append("line")
      .attr("class", "axis-line y-axis") // Klasse für die Y-Achse
      .attr("x1", margin.left)
      .attr("x2", margin.left)
      .attr("y1", height - margin.bottom)
      .attr("y2", margin.top);

    // --- Ticks und Labels ---
    // X-Achse Ticks
    // X-Achse Ticks - maximal 4, gleichmäßig verteilt
    const maxTicks = 4;
    let xTicks = x.ticks(6); // Generiere erstmal mehr Ticks

    if (xTicks.length > maxTicks) {
      const step = Math.ceil(xTicks.length / maxTicks);
      xTicks = xTicks.filter((_, i) => i % step === 0).slice(0, maxTicks);
    }

    xTicks.forEach((tick) => {
      svg
        .append("line")
        .attr("class", "tick-line x-tick") // Klasse für X-Ticks
        .attr("x1", x(tick))
        .attr("x2", x(tick))
        .attr("y1", height - margin.bottom)
        .attr("y2", height - margin.bottom + 5);

      svg
        .append("text")
        .attr("class", "tick-label x-label") // Klasse für X-Labels
        .attr("x", x(tick))
        .attr("y", height - margin.bottom + 15)
        .text(d3.timeFormat("%H:%M")(tick));
    });

    // Y-Achse Ticks
    const yTicks = y.ticks(3);
    yTicks.forEach((tick) => {
      svg
        .append("line")
        .attr("class", "tick-line y-tick") // Klasse für Y-Ticks
        .attr("x1", margin.left - 5)
        .attr("x2", margin.left)
        .attr("y1", y(tick))
        .attr("y2", y(tick));

      svg
        .append("text")
        .attr("class", "tick-label y-label") // Klasse für Y-Labels
        .attr("x", margin.left - 10)
        .attr("y", y(tick) + 3)
        .text(tick);
    });

    // --- Linie ---
    for (let i = 0; i < timeseries.length - 1; i++) {
      const p1 = timeseries[i];
      const p2 = timeseries[i + 1];
      if (p1.value != null && p2.value != null) {
        drawGradientSegment(p1, p2, /* dashed = */ true);
      }
    }

    // --- "Aktuell" als X-Achsen-Beschriftung ---
    const first = timeseries[0];
    // --- "Aktuell" als X-Achsen-Beschriftung ---
    // svg
    //   .append("text")
    //   .attr("x", x(first.date))
    //   .attr("y", height - margin.bottom + 18)
    //   .attr("font-size", "12px")
    //   .attr("font-weight", "bold")
    //   .attr("fill", "#b56332")
    //   .attr("text-anchor", "middle")
    //   .text("Aktuell");

    // --- Punkt und Wert-Label am ersten Wert (immer im Vordergrund!) ---
    const labelGroup = svg.append("g").attr("class", "fixed-labels");

    labelGroup
      .append("circle")
      .attr("cx", x(first.date))
      .attr("cy", y(first.value))
      .attr("r", 3.5)
      .attr("fill", "var(--dv-black)");

    // Wert-Label am ersten Punkt (mit weißem Rand)
    labelGroup
      .append("text")
      .attr("class", "tooltip-chart-label") // Spezifische Klasse für das Label
      .attr("x", x(first.date) + 5)
      .attr("y", y(first.value) - 5)
      .text(Math.round(first.value));

    labelGroup
      .append("text")
      .attr("x", x(first.date) + 5)
      .attr("y", y(first.value) - 5)
      .attr("font-size", "13px")
      .attr("font-weight", "bold")
      .attr("fill", "var(--dv-black)")
      .attr("text-anchor", "start")
      .text(Math.round(first.value));

    svg
      .append("rect")
      .attr("width", width)
      .attr("height", height)
      .attr("fill", "none")
      .attr("pointer-events", "all")
      .on("mousemove", mousemove)
      .on("mouseleave", mouseleave);

    // Tooltip-Fokusgruppe
    const focus = svg.append("g").attr("class", "dv-chart-tooltip").style("display", "none");
    focus.append("circle").attr("class", "tooltip-point").attr("r", 4);
    focus.append("text").attr("class", "tooltip-date").attr("y", 0);
    focus.append("text").attr("class", "tooltip-value").attr("y", 18);

    // Hover-Handler
    function mousemove(event) {
      const [mx] = d3.pointer(event);
      const bisect = d3.bisector((d) => d.date).left;
      const x0 = x.invert(mx);
      const idx = bisect(timeseries, x0);
      const d0 = timeseries[idx - 1];
      const d1 = timeseries[idx];
      const d = !d0 || (d1 && x0 - d0.date > d1.date - x0) ? d1 : d0;

      if (!d) {
        // Kein Wert unter dem Cursor: Feste Labels anzeigen, Tooltip-Hover ausblenden
        svg.select(".fixed-labels").style("display", null);
        focus.style("display", "none");
        return;
      }

      // Wert gefunden: Feste Labels ausblenden, Tooltip-Hover anzeigen
      svg.select(".fixed-labels").style("display", "none");
      focus.style("display", null);

      focus.attr("transform", `translate(${x(d.date)},${y(d.value)})`);

      // Tooltip-Positionierung wie im Hauptchart
      const tooltipWidth = 100;
      let tooltipOffsetX = 10;
      let textAnchor = "start";
      if (x(d.date) + tooltipWidth > width - margin.right) {
        tooltipOffsetX = -10;
        textAnchor = "end";
      }

      focus
        .select(".tooltip-date")
        .attr("x", tooltipOffsetX)
        .attr("text-anchor", textAnchor)
        .text(d3.timeFormat("%d.%m. %H:%M")(d.date));

      focus
        .select(".tooltip-value")
        .attr("x", tooltipOffsetX)
        .attr("text-anchor", textAnchor)
        .text(Math.round(d.value));
    }

    function mouseleave() {
      svg.select(".fixed-labels").style("display", null);
      focus.style("display", "none");
    }
  }
</script>

<div class="map-container">
  <div class="subline">Aktuell gemessene Ozonwerte in µg/m³</div>
  <div class="map-legend">
    <div class="legend-title">Stundenmittel in µg/m³</div>
    <div class="legend-scale">
      <span class="legend-min">{legendMin}</span>
      <div class="gradient-bar"></div>
      <span class="legend-max">{legendMax}</span>
    </div>
  </div>
  <div class="map-wrapper">
    <div class="zoom-buttons">
      <button class="zoom-in-out" on:click={zoomIn} aria-label="Reinzoomen">+</button>
      <button class="zoom-in-out" on:click={zoomOut} aria-label="Rauszoomen">-</button>
      <button on:click={resetZoom} class="reset-btn" aria-label="Karte zurücksetzen">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" class="reset-icon">
          <!-- Oben links -->
          <path
            d="M3 8V5a2 2 0 0 1 2-2h3"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <!-- Oben rechts -->
          <path
            d="M21 8V5a2 2 0 0 0-2-2h-3"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <!-- Unten links -->
          <path
            d="M3 16v3a2 2 0 0 0 2 2h3"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <!-- Unten rechts -->
          <path
            d="M21 16v3a2 2 0 0 1-2 2h-3"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
    </div>
    <!-- Map SVG -->
    <svg bind:this={svg}></svg>
    <div id="hex-tooltip" class="hex-tooltip" style="display: none;"></div>
    <div id="tooltip-chart" style="margin-top:8px"></div>
  </div>
</div>

<style>
</style>
